/**
 * Turns an equation's text into tokens (plan §5.1).
 *
 * The typographic characters a spreadsheet or a Word document leaves behind — `×`, `÷`, `−`, `–` — are
 * normalised to their ASCII operators first, because an admin who pastes a formula out of the workbook
 * should not have to know why the editor rejected a minus sign that looks exactly right.
 */
import { throwEngineError } from '../errors.js';
/** A hand-written expression stays readable; anything longer is a program, not a price rule. */
export const MAX_EXPRESSION_LENGTH = 1_600;
export const MAX_TOKENS = 400;
const OPERATORS = ['<=', '>=', '==', '!=', '+', '-', '*', '/', '^', '<', '>'];
const NUMBER_PATTERN = /^(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?/;
const IDENTIFIER_PATTERN = /^[A-Za-z_][A-Za-z_0-9]*/;
/**
 * `×` → `*`, `÷` → `/`, `−` (U+2212) and `–` (U+2013) → `-`. Every replacement is one character for one
 * character, so token offsets still point at the right place in what the admin typed.
 */
export const normalizeExpression = (source) => String(source).replace(/[−–—]/g, '-').replace(/×/g, '*').replace(/÷/g, '/');
export const tokenize = (source) => {
    const text = normalizeExpression(source);
    if (text.trim() === '') {
        return throwEngineError('SYNTAX', 'The expression is empty');
    }
    if (text.length > MAX_EXPRESSION_LENGTH) {
        return throwEngineError('SYNTAX', `The expression is too long (${text.length} characters; the maximum is ${MAX_EXPRESSION_LENGTH})`);
    }
    const tokens = [];
    let at = 0;
    while (at < text.length) {
        const character = text[at];
        if (/\s/.test(character)) {
            at += 1;
            continue;
        }
        const rest = text.slice(at);
        const number = NUMBER_PATTERN.exec(rest);
        if (number) {
            tokens.push({ type: 'number', value: number[0], start: at, end: at + number[0].length });
            at += number[0].length;
            continue;
        }
        const identifier = IDENTIFIER_PATTERN.exec(rest);
        if (identifier) {
            tokens.push({ type: 'identifier', value: identifier[0], start: at, end: at + identifier[0].length });
            at += identifier[0].length;
            continue;
        }
        if (character === '(' || character === ')' || character === ',') {
            const type = character === '(' ? 'lparen' : character === ')' ? 'rparen' : 'comma';
            tokens.push({ type, value: character, start: at, end: at + 1 });
            at += 1;
            continue;
        }
        const operator = OPERATORS.find((candidate) => rest.startsWith(candidate));
        if (operator) {
            tokens.push({ type: 'operator', value: operator, start: at, end: at + operator.length });
            at += operator.length;
            continue;
        }
        return throwEngineError('SYNTAX', `Unsupported character "${character}" at position ${at + 1}`, { position: at, token: character });
    }
    if (tokens.length > MAX_TOKENS) {
        return throwEngineError('SYNTAX', `The expression is too complex (${tokens.length} tokens; the maximum is ${MAX_TOKENS})`);
    }
    return tokens;
};
//# sourceMappingURL=tokenizer.js.map